import {
  AppLinksSettings,
  CMOnboardingStatus,
  Platform,
  PublicFeatureFlags,
  ScanStatus,
  SSNValidationStatus,
  SubscriberOnboardingStatus,
} from 'mcsclient/models';
import { isAndroid, isIOS, isMacOS, isWindows } from 'store/webView/webViewHelpers';

import { localStorageService } from './LocalStorageService';
import { emailRegex } from './RegexConstants';
import { localeIgnoreCaseEquals } from './String';

export interface IUserData {
  displayName: string;
  userPrincipalName: string;
  userId: string;
  csid: string;
}

export const unknownUser = { displayName: '', userPrincipalName: '', userId: '' } as IUserData;

export interface IServerTiming {
  /**
   * name of performance data entry
   */
  name: string;

  /**
   * duration in milliseconds
   */
  durationMilliseconds: number;
}

export interface ITargetedGeoFencingData {
  showITPOnboarding: boolean;
  countryOrRegionCode: string;
}

export interface IOneTimeScanAppStatusData {
  isScanRetrievalAvailable: boolean;
  scanStatus: ScanStatus;
  retrievalDateTime: string;
  isRegistrationAvailable: boolean;
}

export type RewardsActivity =
  | 'ITMOnboard'
  | 'AcquireLicense'
  | 'OneTimeScan'
  | 'EnableProtectionSetting'
  | 'NewDevice'
  | 'RetainLicense'
  | 'CreditMonitoring'
  | 'FamilyNewDevice';

export interface IRewardsCampaignStatus {
  activity: RewardsActivity;
  isUserRegistered: boolean;
  isUserRewarded: boolean;
  targetPoints: number;
}

export interface ICopilotSettings {
  useCopilotService: boolean;
  systemPrompt: string;
  temperature: number;
  topP: number;
  seed: number;
  maxChatHistorySize: number;
  maxQuestionLength: number;
}

export const enum UnsubscribeStatus {
  Success = 'Success',
  Failed = 'Failed',
  AlreadyUnsubscribed = 'AlreadyUnsubscribed',
}

export const enum NotificationFeature {
  DarkWebMonitoring = 'DarkWebMonitoring',
  CreditMonitoring = 'CreditMonitoring',
  DeviceProtection = 'DeviceProtection',
  ProductRecommendation = 'ProductRecommendation',
  MonthlySecurityDigest = 'MonthlySecurityDigest',
}

export interface IUnsubscribeData {
  userId: string;
  token: string;
  email: string;
  notificationFeature: NotificationFeature;
  isTokenExpired: boolean;
  status: UnsubscribeStatus;
}

/**
 * Represents the application data in the initial payload
 */
export interface IApplicationData {
  userData: IUserData;
  requestLocale: string;
  isRtlLocale: boolean;
  appInsightsKey: string;
  appInsightsConnectionString: string;
  consumerSecurityPortalHost: string;
  consumerSecurityCopilotHost: string;
  isWebView: boolean;
  itpOnboardingStatus: SubscriberOnboardingStatus;
  cmOnboardingStatus: CMOnboardingStatus;
  ssnValidationStatus: SSNValidationStatus;
  targetedGeoFencingData: ITargetedGeoFencingData;
  featureFlags: PublicFeatureFlags;
  serverTimings: IServerTiming[];
  environment: string;
  applicationVersion: string;
  traceIdentifier: string;
  htmlVersion: string;
  downloadLinks: AppLinksSettings;
  appLinks: AppLinksSettings;
  itpSupportPhoneNumber: string;
  helpCenterUrl: string;
  isItmMaintenanceOnNow: boolean;
  isCmMaintenanceOnNow: boolean;
  itmMaintenanceMessage: string;
  cmMaintenanceMessage: string;
  isCMOnboardingSupported: boolean;
  isClarityNoticeDismissed: boolean;
  reactAppName: string;
  // this can be null if user has an active subscription:
  oneTimeCyberAgentScanAppStatusData: IOneTimeScanAppStatusData | null;
  rewardsContext: IRewardsCampaignStatus | null;
  showITMOnPaywall: boolean;
  showPrivacyProtectionOnPaywall: boolean;
  hasActiveLicense: boolean;
  copilotSettings: ICopilotSettings;
  unsubscribeData: IUnsubscribeData | null;
}

class PageDataService {
  private appData: IApplicationData;

  private path = '';

  private queryString = '';

  private queryParameters: Record<string, string> = {};

  private queryStringForTelemetry = '';

  private queryParametersForTelemetry: Record<string, string> = {};

  constructor() {
    const element = document.getElementById('app-data');
    const unloadedFeatureFlags = {} as PublicFeatureFlags;

    if (element) {
      const data = JSON.parse(element.innerText);
      this.appData = data;

      this.appData.userData = data.userData || unknownUser;
      this.appData.appInsightsKey = data.appInsightsKey || '';
      this.appData.appInsightsConnectionString = data.appInsightsConnectionString || '';
      this.appData.consumerSecurityPortalHost = data.consumerSecurityPortalHost || '';
      this.appData.consumerSecurityCopilotHost = data.consumerSecurityCopilotHost || '';
      this.appData.featureFlags = data.featureFlags || unloadedFeatureFlags;
      this.appData.serverTimings = data.serverTimings || [];
      this.appData.environment = data.environment || '';
      this.appData.applicationVersion = data.applicationVersion || '';
      this.appData.htmlVersion = data.htmlVersion || '';
    } else {
      this.appData = {} as IApplicationData;
      this.appData.userData = unknownUser;
      this.appData.appInsightsKey = '';
      this.appData.appInsightsConnectionString = '';
      this.appData.consumerSecurityPortalHost = '';
      this.appData.consumerSecurityCopilotHost = '';
      this.appData.isWebView = false;
      this.appData.featureFlags = unloadedFeatureFlags;
      this.appData.serverTimings = [];
      this.appData.environment = '';
      this.appData.applicationVersion = '';
      this.appData.htmlVersion = '';
      this.appData.downloadLinks = {} as AppLinksSettings;
      this.appData.appLinks = {} as AppLinksSettings;
    }

    if (this.appData.userData.userId && !this.appData.userData.csid) {
      // csid could be missing for very first access to Portal.
      // Reload the page in such case to get the current user's csid.
      window.location.reload();
    }

    this.setOriginData();
  }

  private setOriginData(): void {
    this.path = window.location.pathname;
    this.queryString = window.location.search || '';
    const urlParams = new URLSearchParams(this.queryString);
    const urlParamsForTelemetry = new URLSearchParams(this.queryString);

    urlParams.forEach((value, key) => {
      this.queryParameters[key] = value;

      if (emailRegex.test(value)) {
        urlParamsForTelemetry.set(key, 'REDACTED');
      } else {
        this.queryParametersForTelemetry[key] = value;
      }
    });

    const urlParamsForTelemetryString = urlParamsForTelemetry.toString() || '';

    this.queryStringForTelemetry =
      !urlParamsForTelemetryString || urlParamsForTelemetryString.startsWith('?')
        ? urlParamsForTelemetryString
        : `?${urlParamsForTelemetryString}`;
  }

  public getUserData(): IUserData {
    return this.appData.userData;
  }

  public isCurrentUserByUserId(userId: string | undefined): boolean {
    return localeIgnoreCaseEquals(this.appData.userData.userId, userId);
  }

  public isCurrentUser(csid: string | undefined): boolean {
    return localeIgnoreCaseEquals(this.appData.userData.csid, csid);
  }

  public getAppInsightKey(): string {
    return this.appData.appInsightsKey;
  }

  public getAppInsightConnectionString(): string {
    return this.appData.appInsightsConnectionString;
  }

  public getConsumerSecurityPortalHost(): string {
    return this.appData.consumerSecurityPortalHost;
  }

  public getConsumerSecurityCopilotHost(): string {
    if (this.getCopilotSettings()?.useCopilotService) {
      return this.appData.consumerSecurityCopilotHost;
    }
    return this.getConsumerSecurityPortalHost();
  }

  public getAadClientId(): string {
    return this.isProdEnvironment() ? '406e8cbd-5e2c-4229-a6c7-22349372eb06' : 'c5b2bdae-8810-4099-8c65-a60e7d279c06';
  }

  public getScope(): string {
    return this.isProdEnvironment()
      ? 'api://EF7F3AFA-3B6A-4DFA-AD81-5F57E2F85175/consumersecurity.readwrite'
      : 'api://3a97e270-5f27-441e-956b-e14177afcbce/gibraltar.readwrite';
  }

  public getMfaScope(): string {
    return this.isProdEnvironment()
      ? 'api://EF7F3AFA-3B6A-4DFA-AD81-5F57E2F85175/ConsumerSecurity.SensitiveAccessRead'
      : 'api://3a97e270-5f27-441e-956b-e14177afcbce/Gibraltar.SensitiveAccessRead';
  }

  public isWebView(): boolean {
    return this.appData.isWebView || localStorageService.getItem('forceIsWebView') === 'true';
  }

  public getItpOnboardingStatus(): SubscriberOnboardingStatus {
    return this.appData.itpOnboardingStatus;
  }

  public isITPOnboarded(): boolean {
    return this.getItpOnboardingStatus() === 'Completed';
  }

  public getCMOnboardingStatus(): CMOnboardingStatus {
    return this.appData.cmOnboardingStatus;
  }

  public getSSNValidationStatus(): SSNValidationStatus {
    return this.appData.ssnValidationStatus;
  }

  public isCMOnboardingSupported(): boolean {
    return this.appData.isCMOnboardingSupported;
  }

  public isCMOnboarded(): boolean {
    return this.getCMOnboardingStatus() === 'Monitoring';
  }

  public isITPSupported(): boolean {
    return this.isITPOnboarded() || this.appData.targetedGeoFencingData?.showITPOnboarding;
  }

  public isCopilotEnabled(): boolean {
    return !!this.getFlightSetting().copilotApi;
  }

  public isCMAuthCheckEnabled(platform: Platform | undefined, supportsLocalAuth: boolean): boolean {
    const flightSetting = this.getFlightSetting();

    if (!this.isWebView()) {
      return !!flightSetting.cmAuthCheckWeb;
    }

    // WebView context but client doesn't support local auth (old clients)
    if (!supportsLocalAuth) {
      return false;
    }

    if (isWindows(platform)) {
      return !!flightSetting.cmAuthCheckWindows;
    }

    if (isMacOS(platform)) {
      return !!flightSetting.cmAuthCheckMacOs;
    }

    if (isIOS(platform)) {
      return !!flightSetting.cmAuthCheckIOS;
    }

    if (isAndroid(platform)) {
      return !!flightSetting.cmAuthCheckAndroid;
    }

    return false;
  }

  public getFlightSetting(): PublicFeatureFlags {
    return this.appData.featureFlags;
  }

  public getITPSupportPhoneNumber(): string {
    return this.appData.itpSupportPhoneNumber;
  }

  public getHelpCenterUrl(): string {
    return this.appData.helpCenterUrl;
  }

  public isItmMaintenanceMode(): boolean {
    return this.appData.isItmMaintenanceOnNow || localStorageService.getItem('isItmMaintenanceMode') === 'true';
  }

  public isCmMaintenanceMode(): boolean {
    return this.appData.isCmMaintenanceOnNow || localStorageService.getItem('isCmMaintenanceMode') === 'true';
  }

  public getItmMaintenanceMessage(): string {
    return this.appData.itmMaintenanceMessage;
  }

  public getCmMaintenanceMessage(): string {
    return this.appData.cmMaintenanceMessage;
  }

  public getServerTimings(): IServerTiming[] {
    return this.appData.serverTimings;
  }

  public getEnvironment(): string {
    return this.appData.environment;
  }

  public isDevEnvironment(): boolean {
    return !this.appData.environment || this.appData.environment === 'Development';
  }

  public isPPEEnvironment(): boolean {
    return this.appData.environment?.includes('-ppe-');
  }

  public isProdEnvironment(): boolean {
    return this.appData.environment?.includes('-prod-');
  }

  public getApplicationVersion(): string {
    return this.appData.applicationVersion;
  }

  public getHtmlVersion(): string {
    return this.appData.htmlVersion;
  }

  public getDownloadLinks(): AppLinksSettings {
    return this.appData.downloadLinks;
  }

  public getAppLinks(): AppLinksSettings {
    return this.appData.appLinks;
  }

  public getRequestLocale(): string {
    return this.appData.requestLocale;
  }

  public getCountryOrRegionCode(): string {
    return this.appData.targetedGeoFencingData?.countryOrRegionCode;
  }

  public isRtlLocale(): boolean {
    return this.appData.isRtlLocale || (!this.isProdEnvironment() && localStorageService.getItem('rtlEnabled') === 'true');
  }

  public getQueryParameters(): Record<string, string> {
    return this.queryParameters;
  }

  public getQueryString(): string {
    return this.queryString;
  }

  public getQueryParametersForTelemetry(): Record<string, string> {
    return this.queryParametersForTelemetry;
  }

  public getQueryStringForTelemetry(): string {
    return this.queryStringForTelemetry;
  }

  public getPath(): string {
    return this.path;
  }

  public resetOrigin(): void {
    this.setOriginData();
  }

  public isClarityNoticeDismissed(): boolean {
    return this.appData.isClarityNoticeDismissed;
  }

  public getReactAppName(): string {
    return this.appData.reactAppName;
  }

  public isITMShownOnPaywall(): boolean {
    return this.appData.showITMOnPaywall;
  }

  public isPrivacyProtectionShownOnPaywall(): boolean {
    return this.appData.showPrivacyProtectionOnPaywall;
  }

  public getOneTimeCyberAgentScanAppStatusData(): IOneTimeScanAppStatusData | null {
    return this.appData.oneTimeCyberAgentScanAppStatusData;
  }

  public hasActiveOneTimeScanResult(): boolean {
    const statusData = this.appData.oneTimeCyberAgentScanAppStatusData;

    // If true, user has an active scan result to retrieve.
    // If false, user is either licensed (because oneTimeCyberAgentScanAppStatusData was null),
    // or user doesn't have a scan available.
    return !!statusData?.isScanRetrievalAvailable;
  }

  public hasUsedOneTimeScan(): boolean {
    const statusData = this.appData.oneTimeCyberAgentScanAppStatusData;
    // if true, person has an 'Active' | 'Expired' | 'ExpiredPreCutoff' | 'Upgraded' status
    return !!(statusData?.scanStatus !== 'NotStarted');
  }

  public isUnauthOneTimeScanUsingFakeBreaches(): boolean {
    return !this.isProdEnvironment() && localStorageService.getItem('isUnauthOTSUsingFakeBreaches') === 'true';
  }

  public isUnauthOTSEmailVerificationRequired(): boolean {
    return this.isProdEnvironment() || localStorageService.getItem('skipUnauthOTSEmailVerification') !== 'true';
  }

  public hasActiveSubscription(): boolean {
    // TODO: Remove oneTimeCyberAgentScanAppStatusData check once portal is deployed with appData.hasActiveLicense change
    return !this.appData.oneTimeCyberAgentScanAppStatusData || this.appData.hasActiveLicense;
  }

  public getRewardsContext(activity?: RewardsActivity): IRewardsCampaignStatus | null {
    const { rewardsContext } = this.appData;
    if (activity) {
      return rewardsContext?.activity === activity ? rewardsContext : null;
    }
    return rewardsContext;
  }

  public getCopilotSettings(): ICopilotSettings | null {
    return this.appData.copilotSettings;
  }

  public getUnsubscribeData(): IUnsubscribeData | null {
    return this.appData.unsubscribeData;
  }
}

export const pageDataService = new PageDataService();
